import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import API from "../../redux/api";
import { API_URL } from "../../utils/config";

import { toast } from "react-toastify";
import { encryptText } from "./encryptionUtil";

const initialState = {
  isAuthChecking: true,
  isLoggedIn: false,
  isSessionExpired: false,
  role: "",
  status: "",
  userId: "",
  username: "",
  isLoading: false,
  loginError: null,
};

export const loginUser = createAsyncThunk(
  "login/User",
  async (loginData, { rejectWithValue }) => {
    try {
      if (
        (process.env.REACT_APP_ENVIRONMENT === "production" || process.env.REACT_APP_ENVIRONMENT === "uat") &&
        loginData?.email?.includes("@mosaicinsurance.com")
      ) {
        return rejectWithValue("Access denied. Please sign in using Microsoft.");
      }

      const encryptionKey = process.env.REACT_APP_ENCRYPTION_KEY; // Get from env vars

      // Encrypt password and update payload
      const encryptedPassword = encryptText(loginData?.password, encryptionKey);

      const updatedLoginData = {
        ...loginData,
        password: encryptedPassword,
      };

      const res = await API.post(API_URL + "users/login/", updatedLoginData);
      const data = await res.data;
      return data;
    } catch (error) {
      const errorMessage = error?.response?.data?.detail ?? error?.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchAzureAccessToken = createAsyncThunk(
  "login/oAuthToken",
  async (authorization_code, { rejectWithValue }) => {
    try {
      let payload = {
        "authorization_code": authorization_code
      }

      const res = await API.post(
        API_URL + "users/oauth_login/",
        payload
      );
      const data = await res.data;

      return data;
    } catch (error) {
      const errorMessage = error?.response?.data?.detail ?? error?.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const authChecker = createAsyncThunk(
  "login/authChecker",
  async (_, { rejectWithValue }) => {
    try {
      const res = await API.get(API_URL + "users/auth_check/");
      const data = await res.data;
      return data;
    } catch (error) {
      const errorMessage = error?.response?.data?.detail ?? error?.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const logout = createAsyncThunk(
  "auth_logout/",
  async (_, { rejectWithValue }) => {
    try {
      const user_id = localStorage.getItem("userID");

      const res = await API.post(API_URL + "users/logout/", {
        user_id,
      });

      const data = await res.data;

      // ✅ Broadcast logout to other tabs
      localStorage.clear();
      localStorage.setItem("force-logout", Date.now().toString());

      return data;
    } catch (error) {
      const errorMessage = error?.response?.data?.detail ?? error?.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const loginSlice = createSlice({
  name: "login",
  initialState,
  reducers: {
    setIsLoggedIn(state, action) {
      state.isLoggedIn = action.payload;
    },
    setIsSessionExpired(state, action) {
      state.isSessionExpired = action.payload;
    }
  },
  extraReducers: (builder) => {
    // Login with password
    builder.addCase(loginUser.pending, (state) => {
      state.isLoading = true;
      state.isLoggedIn = false;
    });
    builder.addCase(loginUser.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isLoggedIn = true;
      state.loginError = null;
      state.role = action.payload.role;
      state.status = action.payload.status;
      state.userId = action.payload.user_id;
      state.username = action.payload.username;
      localStorage.setItem("userID", action.payload.user_id);
    });
    builder.addCase(loginUser.rejected, (state, action) => {
      state.isLoading = false;
      state.loginError = action?.payload;
      toast.error(action?.payload || "Something went wrong");
    });

    // OAuth Login
    builder.addCase(fetchAzureAccessToken.pending, (state) => {
      state.isLoading = true;
      state.isLoggedIn = false;
    });
    builder.addCase(fetchAzureAccessToken.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isLoggedIn = true;
      state.loginError = null;
      state.role = action.payload.role;
      state.status = action.payload.status;
      state.userId = action.payload.user_id;
      state.username = action.payload.username;
      localStorage.setItem("userID", action.payload.user_id);
    });
    builder.addCase(fetchAzureAccessToken.rejected, (state, action) => {
      state.isLoading = false;
      state.loginError = action?.payload;
      toast.error(action?.payload || "Something went wrong");
    });

    //auth checker
    builder.addCase(authChecker.pending, (state) => {
      state.isAuthChecking = true;
      state.isLoggedIn = false;
    });
    builder.addCase(authChecker.fulfilled, (state, action) => {
      state.isAuthChecking = false;
      state.isLoggedIn = true;
      state.userId = action.payload.user_id;
    });
    builder.addCase(authChecker.rejected, (state, action) => {
      state.isAuthChecking = false;
      state.isLoggedIn = false;
    });

    //logout
    builder.addCase(logout.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(logout.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isLoggedIn = false;
    });
    builder.addCase(logout.rejected, (state, action) => {
      state.isLoading = false;
    });
  },
});

export const { setIsLoggedIn, setIsSessionExpired } = loginSlice.actions;
export default loginSlice.reducer;
